import { Icon } from '@blueprintjs/core'
import { Tooltip } from '../ui/tooltip'
import classNames from 'classnames'
import { makeAutoObservable } from 'mobx'
import { observer } from 'mobx-react-lite'
import React, { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { fetchUserIfNeeded } from '../../actions/user'
import { NavStateType } from '../../constants/reducers/nav'
import { useUser } from '../../hooks/useUser'
import { MerchjarLogo } from '../../MerchjarLogo'
import { UserStore, userStore } from '../../stores/user_store'
import type { Models } from '../../types/profile'
import { Navbar } from '../navbar/Navbar'
import { NavbarBottomSection } from '../navbar/NavbarBottomSection'
import { ProfileSelect } from '../navbar/ProfileSelect'
type Profile = Models.Profile

interface TopCalloutProps {
    children: JSX.Element | any[]
    id?: string
    className?: string
    bottomBorder?: boolean
    icon?: JSX.Element
    height?: number
}

export const CALLOUT_HEIGHT = 49

export const TopCallout = ({
    children,
    id,
    className,
    bottomBorder = true,
    icon = undefined,
    height = CALLOUT_HEIGHT,
}: TopCalloutProps) => {
    let classNames = 'bg-blue-50 px-2 py-1 flex items-center justify-center w-full h-10 left-0'
    if (bottomBorder) {
        classNames += ' border-b'
    }
    if (className) {
        classNames += ` ${className}`
    }
    return (
        <div id={id} className={classNames} style={{ height: height ? height : '' }}>
            {icon ? (
                icon
            ) : (
                <Icon
                    icon={<Icon icon="info-sign" className="text-blue-500 mr-2" />}
                    className="text-blue-500 mr-2"
                />
            )}
            {children}
        </div>
    )
}

interface NavProps {
    // isPinned: boolean
    hasUser: boolean
    // isHovered: boolean
    // open: boolean
    location: string
    activeProfile?: Profile
    profiles: Profile[]
    calloutsCount: number
    hideProfileAndPin?: boolean
}

export const Nav = observer(
    ({
        hasUser,
        location,
        activeProfile,
        profiles,
        calloutsCount,
        hideProfileAndPin = false,
    }: NavProps) => {
        const isPinned = navStore.isPinned
        const isHovered = navStore.isHovered
        const open = navStore.open

        const select = useRef<HTMLDivElement>(null)
        const bottom = useRef<HTMLDivElement>(null)
        const [height, setHeight] = useState(0)

        const burgerMenu = useMemo(() => {
            return (
                <Tooltip hoverOpenDelay={300} content={<span>Open sidebar</span>}>
                    <div style={{ height: '67.5px' }} className="flex items-center w-full">
                        <div
                            className="flex items-center mj-menu-item cursor-pointer w-full"
                            onClick={() => navStore.setOpen(true)}
                        >
                            <Icon icon="menu" />
                        </div>
                    </div>
                </Tooltip>
            )
        }, [])

        const logo = useMemo(
            () => <Logo showPinToggle={!hideProfileAndPin} />,
            [isPinned, hideProfileAndPin],
        )

        const navWidth = useMemo(() => {
            if (!hasUser || !open || isHovered) {
                return 0
            }
            return '16em'
        }, [open, hasUser, isHovered])

        const memoizedNavbar = useMemo(
            () => <Navbar location={location} />,
            [location],
        )

        // To get the actual navbar items to be scrollable when they overflow, we need
        // to pass an explicit height value into the overflow-y-auto div. This means
        // we need to take a ref to the profile select and bottom divs, then calculate
        // how much space is left between them.
        //
        // The layout effect hook necessary to ensure that when the
        // nav is toggled that the height is recalculated.
        useLayoutEffect(() => {
            const selectRect = select.current?.getBoundingClientRect()
            const bottomRect = bottom.current?.getBoundingClientRect()

            if (selectRect && bottomRect) {
                const height = window.innerHeight
                const top = selectRect.height + selectRect.y
                const bottom = bottomRect.height

                if (height != height - top - bottom) {
                    setHeight(height - top - bottom)
                }
            }
        }, [select.current, bottom.current, isPinned])

        if (!isPinned) {
            return <span />
        }

        return (
            <Fragment>
                <div
                    style={{ width: navWidth }}
                    className={classNames(
                        'fixed transform mj-nav',
                        !open
                            ? 'w-16 flex-basis-1 animate-slide-out'
                            : 'visible w-80 flex-shrink-0 flex-basis-80',
                    )}
                >
                    {/* show logo when navbar open and when user is not authenticated (hidden on auth pages) */}
                    {(open || !hasUser) && isPinned && !hideProfileAndPin && logo}

                    {!open && hasUser && burgerMenu}

                    <div className="">
                        <div className="flex flex-col justify-between h-full">
                            {!hideProfileAndPin && (
                                <div ref={select}>
                                    <ProfileSelect id="nav-profile-select" />
                                </div>
                            )}
                            {hasUser && (
                                <>
                                    <div className="overflow-y-auto" style={{ height }}>
                                        {memoizedNavbar}
                                    </div>
                                    <div ref={bottom} className="h-12 bg-white w-full">
                                        <NavbarBottomSection
                                            activeProfile={activeProfile}
                                            profiles={profiles}
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </Fragment>
        )
    },
)

const Logo = observer(({ showPinToggle = true }: { showPinToggle?: boolean }): JSX.Element => {
    const onClick = () => {
        if (!showPinToggle) {
            return
        }

        if (navStore.isPinned) {
            navStore.setState('hidden')
            navStore.setOpen(false)
        } else if (navStore.isHidden || navStore.isHovered) {
            navStore.setState('pinned')
            navStore.setOpen(true)
        }
    }

    return (
        <div
            className={classNames(
                'px-2 py-1.5 flex items-center justify-between transform duration-300',
                'visible opacity-100',
            )}
        >
            <div className="mt-2">
                <MerchjarLogo height={28} width={155} />
            </div>
            {showPinToggle && (
                <Tooltip
                    hoverOpenDelay={300}
                    content={
                        navStore.isPinned ? <span>Close sidebar</span> : <span>Pin side menu</span>
                    }
                >
                    <span onClick={onClick} id="nav-close">
                        <Icon
                            icon={
                                <Icon
                                    color="#3391FF"
                                    icon={
                                        navStore.isPinned
                                            ? 'double-chevron-left'
                                            : 'double-chevron-right'
                                    }
                                    className="mr-2 mt-3 cursor-pointer"
                                />
                            }
                        />
                    </span>
                </Tooltip>
            )}
        </div>
    )
})

interface NavHoverProps {
    isPinned: boolean
    hasUser: boolean
    isHovered: boolean
    open: boolean
    location: string
    activeProfile?: Profile
    profiles: Profile[]
    hideProfileAndPin?: boolean
}

export const NavHover = observer(
    ({
        isPinned,
        hasUser,
        isHovered,
        open,
        location,
        activeProfile,
        profiles,
        hideProfileAndPin = false,
    }: NavHoverProps) => {
        const [isDelayedHidden, setIsDelayedHidden] = useState(!isHovered)

        const burgerMenu = useMemo(() => {
            return (
                <Tooltip hoverOpenDelay={300} content={<span>Open sidebar</span>}>
                    <div style={{ height: '67.5px' }} className="flex items-center w-full">
                        <div
                            className="flex items-center mj-menu-item cursor-pointer w-full"
                            onClick={() => {
                                navStore.setOpen(true)
                                setIsDelayedHidden(true)
                            }}
                        >
                            <Icon icon="menu" />
                        </div>
                    </div>
                </Tooltip>
            )
        }, [])

        const logo = useMemo(
            () => <Logo showPinToggle={!hideProfileAndPin} />,
            [isPinned, hideProfileAndPin],
        )

        useEffect(() => {
            const timer = setTimeout(() => {
                setIsDelayedHidden(!isHovered)
            }, 50)

            return () => clearTimeout(timer)
        }, [isHovered])

        return (
            <div
                className={classNames(
                    'fixed border-t rounded-tr-lg shadow-lg left-0 z-20 h-screen transition-opacity',
                    isHovered && !isPinned ? 'animate-slide-in' : 'animate-slide-out',
                    isDelayedHidden && 'hidden',
                )}
                style={{ width: '16em', top: 80 }}
            >
                <div
                    style={{ width: '16em' }}
                    className={classNames(
                        'absolute',
                        'mj-nav',
                        'rounded-tr-lg',
                        'overflow-y-auto',
                        'w-full',
                        'z-20',
                        !open ? 'w-16 flex-basis-16' : 'visible flex-shrink-0 flex-basis-80',
                        isPinned && 'top-8',
                    )}
                >
                    {/* show logo when navbar open and when user is not authenticated (hidden on auth pages) */}
                    {(open || !hasUser) && isPinned && !hideProfileAndPin && logo}

                    {!open && hasUser && burgerMenu}

                    <div className="flex flex-col h-full w-full z-20">
                        {!hideProfileAndPin && <ProfileSelect />}
                        {hasUser && (
                            <>
                                <div>
                                    <Navbar location={location} />
                                </div>
                                <div className="mt-auto h-12 fixed bottom-0 w-72">
                                    <NavbarBottomSection
                                        activeProfile={activeProfile}
                                        profiles={profiles}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        )
    },
)

class NavStore {
    userStore: UserStore
    open = true
    state: NavStateType = 'pinned'
    /** Height (px) of any visible top banner; 0 when no banner is shown. */
    bannerHeight = 0

    // Accordion state holds a list of all the accordion items that are open
    accordionState: string[] = []

    constructor(userStore: UserStore) {
        const accordionState = localStorage.getItem('accordion-state')
        if (accordionState) {
            this.accordionState = JSON.parse(accordionState)
        } else {
            this.accordionState = []
        }

        this.userStore = userStore
        makeAutoObservable(this)
    }

    get isPinned() {
        return this.state == 'pinned'
    }

    get isHidden() {
        return this.state == 'hidden'
    }

    get isHovered() {
        return this.state == 'hovered'
    }

    setState(state: NavStateType) {
        this.state = state
    }

    setOpen(open: boolean) {
        this.open = open
    }

    setBannerHeight(height: number) {
        this.bannerHeight = height
    }

    setAccordionState(state: string[]) {
        this.accordionState = state
        localStorage.setItem('accordion-state', JSON.stringify(state))
    }

    removeAccordionState(state: string) {
        if (this.accordionState.includes(state)) {
            this.accordionState = this.accordionState.filter((item) => item != state)
            localStorage.setItem('accordion-state', JSON.stringify(this.accordionState))
        }
    }

    addAccordionState(state: string) {
        if (!this.accordionState.includes(state)) {
            this.accordionState.push(state)
            localStorage.setItem('accordion-state', JSON.stringify(this.accordionState))
        }
    }

    shouldBeCollapsed(key: string): boolean {
        return !this.accordionState.includes(key)
    }
}

export const navStore = new NavStore(userStore)

/**
 * Nav Manager is basically a wrapper around the Nav components
 * Its used to easily handle nav's states: pinned, hovered, hidden
 *
 * @return {React.Component}
 */
export const NavManager = observer(({ calloutsCount }: { calloutsCount: number }) => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation().pathname
    const hideProfileAndPin = useMemo(() => {
        return (
            location === '/login' ||
            location === '/register' ||
            location === '/password/forget' ||
            location.startsWith('/password/reset')
        )
    }, [location])

    const { user, marketplace, hasUser, profile, activeProfile, profiles } = useUser()

    if (marketplace) {
        // @ts-ignore
        window.marketplace = marketplace
    }

    useEffect(() => {
        dispatch(fetchUserIfNeeded())

    }, [profile])

    // If user doesn't have keys
    // redirect to /onboard
    useEffect(() => {
        // Don't trap an impersonating admin on /onboard: the keyless account
        // they're viewing would otherwise force-redirect every route back to
        // onboarding, hiding the rest of the app.
        if (user?.impersonating) {
            return
        }
        if (user && user.hasOwnProperty('keys') && !user.keys) {
            console.log('going to onboard due to not keys', user)
            navigate('/onboard')
        }
    }, [user])

    return (
        <>
            {!navStore.isPinned && (
                <NavHover
                    isPinned={navStore.isPinned}
                    hasUser={hasUser}
                    isHovered={navStore.isHovered}
                    open={navStore.open}
                    location={location}
                    activeProfile={activeProfile ?? undefined}
                    profiles={profiles}
                    hideProfileAndPin={hideProfileAndPin}
                />
            )}
            <Nav
                calloutsCount={calloutsCount}
                hasUser={hasUser}
                location={location}
                activeProfile={activeProfile ?? undefined}
                profiles={profiles}
                hideProfileAndPin={hideProfileAndPin}
            />
        </>
    )
})
